/**
 * `RefreshOverlay` — the card-level "refreshing" treatment (loading
 * level 2, plan §2.3): while `refreshing` is true the wrapped content
 * stays MOUNTED and readable but dims (opacity ≈ .75) with a subtle
 * shimmer sweep on top. Nothing unmounts — this is the
 * stale-while-revalidate building block that replaces the hard
 * skeleton swap on refetch (PR-B wires it into the dashboard).
 *
 * Appearance delay (~300ms, plan §5 risk #6 + calibración 2026-07-23): the first-page cache
 * resolves default refetches in ~10ms — the overlay must NOT flash on
 * instant fetches, so it only becomes visible when `refreshing` has
 * been true for `appearDelayMs`. The delay is a real timer (not a CSS
 * transition-delay) so the shimmer never mounts at all for fast
 * fetches.
 *
 * Reduced motion: the dim still applies (state feedback), the shimmer
 * does not (decoration — `RefreshShimmer` returns null on its own).
 */
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  MOTION,
  RefreshShimmer,
  Spinner,
  ensureMotionStyles,
} from "@pervasivemind/bi-components";

export interface RefreshOverlayProps {
  /** True while this card's data is being refetched. */
  refreshing: boolean;
  /** ms `refreshing` must persist before the overlay shows. */
  appearDelayMs?: number;
  /** Block interaction with the stale content while visible. */
  blockPointer?: boolean;
  children: ReactNode;
  className?: string;
  /** Extra classes on the CONTENT wrapper (the dimming div) — pass
   *  `h-full` when the child relies on a percentage-height chain. */
  contentClassName?: string;
}

// Calibración 2026-07-23 (trial junto con el fade-over off): 150ms
// dejaba que fetches de ~0.5s mostraran un dip completo de opacidad —
// leído como "parpadeo sutil". A 300ms los refetches rápidos nunca
// diman; los lentos siguen señalizados (y el pill escala a los 5s).
export const REFRESH_OVERLAY_APPEAR_DELAY_MS = 300;

/** ms `refreshing` must persist before the long-wait pill appears
 *  (opción A, 2026-07-23): heavy unfiltered aggregations take 10-20s
 *  and a static dim+shimmer reads as "stuck". */
export const REFRESH_OVERLAY_LONG_WAIT_MS = 5000;

/** The escalated signal for long fetches: a floating pill with an
 *  accent spinner, "still querying" copy and a live elapsed-seconds
 *  counter — proof the block is alive, not frozen. Driven by a 1s
 *  interval (state feedback, not decoration → runs under reduced
 *  motion too; the spinner handles its own reduced face).
 *
 *  A11y (audit 2026-07-24): the pill is a STATE announcement, and it
 *  used to reach nobody — the only `role="status"` in the subtree was
 *  the Spinner's, whose svg is `aria-hidden` and whose only content is
 *  an aria-label (labels do not fire live-region announcements). A
 *  screen-reader user therefore never learned the query was still
 *  running. Three rules encoded below:
 *    1. The message is announced EXACTLY ONCE, politely.
 *    2. The elapsed counter is NEVER inside the live region — one
 *       announcement per second is unusable noise. It stays
 *       `aria-hidden`: it is visual proof-of-life, not information.
 *    3. The live region mounts EMPTY and receives its text on the next
 *       commit. A live region inserted with its text already inside is
 *       not reliably announced (the region has to exist first, then
 *       mutate) — and this pill only mounts at the 5s mark. */
function LongWaitPill({ startedAt }: { startedAt: number }): JSX.Element {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  // Rule 3 — flip on the commit AFTER mount so the message lands as a
  // real mutation inside an already-present polite region.
  useEffect(() => {
    setAnnounced(true);
  }, []);
  const secs = Math.max(0, Math.round((now - startedAt) / 1000));
  const label = t("dashboard.longwait.label");
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 justify-center"
      data-pm-long-wait
    >
      <div className="flex items-center gap-2 rounded-full bg-navy/90 px-4 py-2 text-xs font-medium text-white shadow-lg">
        {/* Decorative here: the live region below carries the message,
            so the Spinner's own role="status" would be a SECOND region
            announcing the same text. `ariaLabel` is still passed (lib
            contract) — it just never reaches the a11y tree. */}
        <span aria-hidden="true" className="inline-flex">
          <Spinner size={16} ariaLabel={label} />
        </span>
        <span aria-hidden="true">{label}</span>
        <span className="tabular-nums text-white/80" aria-hidden="true">
          {secs}s
        </span>
        <span className="sr-only" aria-live="polite">
          {announced ? label : ""}
        </span>
      </div>
    </div>
  );
}

export function RefreshOverlay({
  refreshing,
  appearDelayMs = REFRESH_OVERLAY_APPEAR_DELAY_MS,
  blockPointer = false,
  children,
  className,
  contentClassName,
}: RefreshOverlayProps): JSX.Element {
  ensureMotionStyles();
  const [visible, setVisible] = useState(false);
  // Long-wait escalation: `startedAt` doubles as the "show the pill"
  // flag and the elapsed-counter epoch.
  const [longWaitStart, setLongWaitStart] = useState<number | null>(null);

  useEffect(() => {
    if (!refreshing) {
      setVisible(false);
      setLongWaitStart(null);
      return undefined;
    }
    const started = Date.now();
    const t = setTimeout(() => setVisible(true), appearDelayMs);
    const tl = setTimeout(
      () => setLongWaitStart(started),
      REFRESH_OVERLAY_LONG_WAIT_MS,
    );
    return () => {
      clearTimeout(t);
      clearTimeout(tl);
    };
  }, [refreshing, appearDelayMs]);

  return (
    <div className={`relative ${className ?? ""}`} data-pm-refresh-overlay>
      <div
        className={contentClassName}
        style={{
          opacity: visible ? 0.75 : 1, // 0.55 → 0.75: dim presente pero sin "bajón"
          transition: `opacity ${MOTION.durBase}ms ${MOTION.easeOut}`,
          pointerEvents: blockPointer && visible ? "none" : undefined,
        }}
        aria-busy={refreshing || undefined}
      >
        {children}
      </div>
      {visible && <RefreshShimmer />}
      {longWaitStart !== null && <LongWaitPill startedAt={longWaitStart} />}
    </div>
  );
}
